import React, { useState, useEffect } from 'react';
import '../styling/containerStyles.css'
import { BackIcon } from '../components/Icons';
import moment from 'moment';
import ReceiptIcon from '@material-ui/icons/Receipt';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CircularProgress from '@material-ui/core/CircularProgress'
import SearchBar from '../components/SearchBar';
import DateFilter from '../components/DateFilter';

interface TransactionHistoryFilterProps {
    viewHistory: boolean;
    setViewHistory: (viewHistory: boolean) => void;
    transactionList: string[];
    transactionHistoryLoading: boolean;
    transactionHistoryData: any;
    transactionTab: string;
    setTransactionTab: (transactionTab: string) => void;
    GetTransactionHistory: (arg: any, search: any, fromDate: string, toDate: string, offset?: number, append?: boolean) => void;
    hasMoreTransactions?: boolean;
    loadingMoreTransactions?: boolean;
    transactionOffset?: number;
}


export const TransactionHistoryFilter: React.FC<TransactionHistoryFilterProps> = (props: TransactionHistoryFilterProps) => {

    const {
        viewHistory, setViewHistory, transactionList, transactionHistoryLoading, transactionHistoryData, transactionTab, setTransactionTab, GetTransactionHistory, hasMoreTransactions = false, loadingMoreTransactions = false, transactionOffset = 0
    } = props;

    const [searchQuery, setSearchQuery] = useState('');
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [activeFromDate, setActiveFromDate] = useState<string>('');
    const [activeToDate, setActiveToDate] = useState<string>('');

    // Clear search and date filters when transaction history is closed or opened
    useEffect(() => {
        if (!viewHistory) {
            // Clear all filters when history is closed
            setSearchQuery('');
            setFromDate('');
            setToDate('');
            setActiveFromDate('');
            setActiveToDate('');
        }
    }, [viewHistory]);

    // Handle Load More button click
    const handleLoadMore = () => {
        if (hasMoreTransactions && !loadingMoreTransactions) {
            // Determine the tab parameter based on current tab
            const tabParam = transactionTab === 'ALL' ? '' : transactionTab.toLowerCase();
            GetTransactionHistory(
                tabParam,
                searchQuery ? searchQuery : '',
                activeFromDate,
                activeToDate,
                transactionOffset,
                true // append mode
            );
        }
    };

    return (
        <div className={viewHistory ? 'transaction-history-wrapper history-open' : "transaction-history-wrapper"}>
            <div className="transaction-history flex flex-column">
                <div className="flex flex-align-center transaction-header">
                    <span className="backIconBtn" onClick={() => {
                        setViewHistory(false);
                        setTransactionTab("ALL");
                        // Clear search and date filters when closing
                        setSearchQuery('');
                        setFromDate('');
                        setToDate('');
                        setActiveFromDate('');
                        setActiveToDate('');
                    }}>
                        <BackIcon />
                    </span>
                    <ReceiptIcon className="transaction-history-icon" />
                    <h3>Transaction History</h3>
                </div>
                <div className="transaction-body flex flex-column flex-auto">
                    <ul className="tabs flex flex-space-around">
                        {transactionList.map((tran, index) => {
                            return (
                                <li key={`${index + 1}`} className={transactionTab == tran ? "active" : ''} onClick={() => {
                                    setTransactionTab(tran);

                                    // Clear search and date filters on tab change
                                    setSearchQuery('');
                                    setFromDate('');
                                    setToDate('');

                                    // Calculate date range based on tab
                                    let fromDateToPass = '';
                                    let toDateToPass = '';

                                    if (tran === 'Last 7 Days') {
                                        // Last 7 days from today
                                        fromDateToPass = moment().subtract(7, 'days').format('YYYY-MM-DD');
                                        toDateToPass = moment().format('YYYY-MM-DD');
                                        setActiveFromDate(fromDateToPass);
                                        setActiveToDate(toDateToPass);
                                    } else if (tran === 'Current Month') {
                                        // This month from start to today
                                        fromDateToPass = moment().startOf('month').format('YYYY-MM-DD');
                                        toDateToPass = moment().format('YYYY-MM-DD');
                                        setActiveFromDate(fromDateToPass);
                                        setActiveToDate(toDateToPass);
                                    } else {
                                        // For ALL and Last 10 Txn, clear active dates
                                        setActiveFromDate('');
                                        setActiveToDate('');
                                        fromDateToPass = '';
                                        toDateToPass = '';
                                    }

                                    GetTransactionHistory(tran == "ALL" ? "" : tran.toLocaleLowerCase(), '', fromDateToPass, toDateToPass)
                                }}><a href="javascript:void(0)" onClick={(e) => e.preventDefault()}>{tran}</a></li>
                            )
                        })}
                    </ul>
                    {(transactionTab === 'ALL' || transactionTab === 'Current Month' || transactionTab === 'Last 7 Days') && (
                        <div className="filters-row">
                            <div className="filters-row-inner">
                                <SearchBar
                                    value={searchQuery}
                                    onChange={(value) => {
                                        setSearchQuery(value);
                                        // Determine the tab parameter based on current tab
                                        const tabParam = transactionTab === 'ALL' ? '' : transactionTab.toLowerCase();
                                        // Reset offset to 0 when searching (not append mode)
                                        GetTransactionHistory(tabParam, value ? value : '', activeFromDate, activeToDate, 0, false);
                                    }}
                                    placeholder="Search Coupons"
                                    autoFocus
                                    className="search-field"
                                    variant="filled"
                                />
                                {transactionTab === 'ALL' && (
                                    <div className="date-filter-wrap">
                                        <DateFilter
                                            fromDate={fromDate}
                                            toDate={toDate}
                                            onFromDateChange={setFromDate}
                                            onToDateChange={setToDate}
                                            variant="standard"
                                            onApply={(from, to) => {
                                                setActiveFromDate(from);
                                                setActiveToDate(to);
                                                // Reset offset to 0 when applying date filter (not append mode)
                                                GetTransactionHistory("", searchQuery ? searchQuery : '', from, to, 0, false);
                                            }}
                                            onClearAll={() => {
                                                setActiveFromDate('');
                                                setActiveToDate('');
                                                setFromDate('');
                                                setToDate('');
                                                // Reset offset to 0 when clearing date filter (not append mode)
                                                GetTransactionHistory("", searchQuery ? searchQuery : '', '', '', 0, false);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {transactionHistoryLoading ? <div className="trans-history-loader"><CircularProgress /></div> :
                        <>
                            <ul className="transaction-list flex-auto">
                                {transactionHistoryData && Array.isArray(transactionHistoryData) && transactionHistoryData.length > 0 ? transactionHistoryData.map((data, index) => {
                                    return (
                                        <li
                                            className="transaction-history-item"
                                            key={`${data.id || index + 1}`}
                                        >
                                            <span className="amount">
                                                <h2>{data.mode_of_payment === "points" ? data.redeem_amount : `₹${data.redeem_amount}`}</h2>
                                                <div className="amount-details">
                                                    <small>{data.coupon_code}</small>
                                                    {/* <small>"Earned"</small> */}
                                                    <small>{data.mode_of_payment}</small>
                                                </div>
                                            </span>
                                            <span className="date-time">
                                                {moment(data.createddate).format('DD/MM/YYYY')}<br></br>{moment(data.createddate).format('HH:mm A')}
                                                <small>{data.status === "processed" || data.status === "SUCCESS" ? "SUCCESS" :
                                                    data.status === "queued" || data.status === "pending" || data.status === "processing" || data.status === "APPROVAL_PENDING" || data.status === "PENDING" || data.status === "QUEUED" || data.status === "RECEIVED" ? "PENDING" :
                                                        "FAILED"}</small>
                                            </span>
                                        </li>
                                    );
                                }) : <span className="tag tag-blue"><InfoOutlinedIcon />No Data Available</span>
                                }
                            </ul>
                            {(transactionTab === 'ALL' || transactionTab === 'Current Month' || transactionTab === 'Last 7 Days') && hasMoreTransactions && (
                                <div className="load-more-btn">
                                    <button
                                        onClick={handleLoadMore}
                                        disabled={loadingMoreTransactions}
                                        className={loadingMoreTransactions ? "load-more-btn-disabled" : "load-more-btn-enabled"}
                                    >
                                        {loadingMoreTransactions ? (
                                            <>
                                                <CircularProgress size={16} className="load-more-btn-loader" />
                                                Loading...
                                            </>
                                        ) : (
                                            "Load More"
                                        )}
                                    </button>
                                </div>
                            )}
                        </>
                    }
                </div>
            </div>
        </div>
    )
}


export default TransactionHistoryFilter